import React, { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Message } from 'primereact/message';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import { GlobalSettingsService } from '../components/services/GlobalSettingsService';
import { DEFAULT_GLOBAL_SETTINGS } from '../constants';
import GlobalSettingsSchema from '../constants/schemas/GlobalSettingsSchema';
import VisibilityWrapper from '../utils/wrappers/VisibilityWrapper';

import DepartmentAssignmentScheduleDialog, {
  DepartmentAssignmentSchedule
} from './DepartmentAssignmentScheduleDialog';
import ConfirmationDialog from './shared/ConfirmationDialog';
import { withDialogFooterSpacing } from './shared/dialogFooterSpacing';
import './css/GlobalSettingsDialog.css';

type FormValues = z.infer<typeof GlobalSettingsSchema>;

interface GlobalSettingsDialogProps {
  visible: boolean;
  onHide: () => void;
}

const GlobalSettingsDialog: React.FC<GlobalSettingsDialogProps> = ({
  visible,
  onHide
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isDirty, errors, isValid }
  } = useForm<FormValues>({
    resolver: zodResolver(GlobalSettingsSchema),
    defaultValues: {
      ...DEFAULT_GLOBAL_SETTINGS
    }
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [
    showDepartmentAssignmentScheduleDialog,
    setShowDepartmentAssignmentScheduleDialog
  ] = useState(false);

  const timesheetSplitHours = watch(
    'timesheet_split_hours_across_calendar_days'
  );
  const twoFactor = watch('two_factor_authentication');
  const distanceBalancedAssignment = watch('distance_balanced_assignment');
  const planDates = watch('plan_dates_department_check');
  const taskTypeDepartmentCheck = watch('taskType_department_check');
  const maxMonthlyScheduledHours = watch(
    'person_shift_restrictions.maxMonthlyScheduledHours'
  );
  const maxConsecutiveDaysWithShifts = watch(
    'person_shift_restrictions.maxConsecutiveDaysWithShifts'
  );
  const departmentAssignmentSchedule = watch('department_assignment_schedule');

  useEffect(() => {
    if (visible) {
      loadSettings();
    }
  }, [visible]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const loadedSettings = await GlobalSettingsService.get();
      reset({
        ...loadedSettings,
        department_assignment_schedule:
          loadedSettings.department_assignment_schedule ?? null
      });
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setSaving(true);
    try {
      await GlobalSettingsService.update(data);
      setShowSaveConfirmation(false);
      onHide();
    } catch (error) {
      console.error('Ошибка сохранения настроек:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowUnsavedDialog(true);
    } else {
      onHide();
    }
  };

  const handleDepartmentAssignmentSave = (
    data: DepartmentAssignmentSchedule | null
  ) => {
    if (data === null) {
      setValue('department_assignment_schedule', null, {
        shouldDirty: true,
        shouldValidate: true
      });
      return;
    }
    setValue(
      'department_assignment_schedule',
      {
        startDate: data.startDate,
        periodValue: data.periodValue,
        periodType: data.periodType,
        daysLookback: data.daysLookback,
        scheduleSettings: data.scheduleSettings
      },
      {
        shouldDirty: true,
        shouldValidate: true
      }
    );
  };

  const handleSaveClick = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (isValid) setShowSaveConfirmation(true);
  };

  const handleConfirmSave = () => {
    setShowSaveConfirmation(false);
    handleSubmit(onSubmit)();
  };

  const handleCancelSave = () => {
    setShowSaveConfirmation(false);
  };

  const handleContinueWithoutSaving = () => {
    setShowUnsavedDialog(false);
    onHide();
  };

  const dialogHeader = (
    <div className="flex justify-content-between align-items-center w-full">
      <span>Общесистемные настройки</span>
    </div>
  );

  return (
    <>
      <Dialog
        header={dialogHeader}
        visible={visible}
        onHide={handleCancel}
        style={{ width: '570px', minWidth: '640px', minHeight: 'fit-content' }}
        modal
        pt={withDialogFooterSpacing()}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Сохранить"
              icon="pi pi-check"
              onClick={handleSaveClick}
              disabled={!isDirty || saving}
              loading={saving}
              type="button"
            />
            <Button
              label="Отмена"
              icon="pi pi-times"
              onClick={handleCancel}
              className="p-button-secondary"
              disabled={saving}
            />
          </div>
        }
      >
        <VisibilityWrapper code="globalSetting-roles">
          <div className="flex flex-column gap-3">
            <div className="flex flex-column gap-3">
              <Message
                severity="error"
                text="Настройки, находящиеся в данном окне, распространяются на всю систему"
                className="w-full"
              />

              <div className="globalSettings-row flex align-items-center justify-content-between gap-3">
                <label htmlFor="hours-division-switch" className="font-medium">
                  Делить часы табеля по календарным суткам
                </label>
                <InputSwitch
                  id="hours-division-switch"
                  checked={timesheetSplitHours}
                  {...register('timesheet_split_hours_across_calendar_days')}
                  disabled={loading || saving}
                />
              </div>

              <div className="globalSettings-row flex align-items-center justify-content-between gap-3">
                <label htmlFor="shift-start-switch" className="font-medium">
                  Все часы по табелю относятся к дню начала смены
                </label>
                <InputSwitch
                  id="shift-start-switch"
                  checked={!timesheetSplitHours}
                  onChange={(e) =>
                    setValue(
                      'timesheet_split_hours_across_calendar_days',
                      !(e.value ?? false),
                      {
                        shouldDirty: true
                      }
                    )
                  }
                  disabled={loading || saving}
                />
              </div>

              <div className="globalSettings-row flex align-items-center justify-content-between gap-3">
                <label htmlFor="planDates" className="font-medium">
                  Учитывать плановые сроки по нарядам при распределении на
                  участки
                </label>
                <InputSwitch
                  id="planDates"
                  checked={planDates}
                  {...register('plan_dates_department_check')}
                  disabled={loading || saving}
                />
              </div>

              <div className="globalSettings-row flex align-items-center justify-content-between gap-3">
                <label
                  htmlFor="taskTypeDepartmentCheck"
                  className="font-medium"
                >
                  Учитывать специализацию участков
                </label>
                <InputSwitch
                  id="taskTypeDepartmentCheck"
                  checked={taskTypeDepartmentCheck}
                  {...register('taskType_department_check')}
                  disabled={loading || saving}
                />
              </div>

              <div className="globalSettings-row flex align-items-center justify-content-between gap-3">
                <label htmlFor="twoFactor" className="font-medium">
                  Использовать 2FA авторизацию
                </label>
                <InputSwitch
                  id="twoFactor"
                  checked={twoFactor}
                  {...register('two_factor_authentication')}
                  disabled={loading || saving}
                />
              </div>
              <div className="globalSettings-row flex align-items-center justify-content-between gap-3">
                <label
                  htmlFor="distanceBalancedAssignment"
                  className="font-medium"
                  style={{ maxWidth: 500 }}
                >
                  Равномерное распределение нарядов по сотрудникам при
                  оптимизации преодолеваемого расстояния
                </label>
                <InputSwitch
                  id="distanceBalancedAssignment"
                  checked={distanceBalancedAssignment}
                  {...register('distance_balanced_assignment')}
                  disabled={loading || saving}
                />
              </div>
            </div>
            <div>
              <div className="flex flex-column">
                <div className="globalSettings-row flex align-items-center justify-content-between gap-3">
                  <label htmlFor="maxConsecutiveDays" className="font-medium">
                    Максимальное количество смен подряд
                  </label>
                  <div
                    className="global-settings-input-wrapper"
                    style={{ width: '50px' }}
                  >
                    <InputNumber
                      id="maxConsecutiveDays"
                      value={maxConsecutiveDaysWithShifts}
                      onValueChange={(e) =>
                        setValue(
                          'person_shift_restrictions.maxConsecutiveDaysWithShifts',
                          e.value ?? 0,
                          {
                            shouldDirty: true,
                            shouldValidate: true
                          }
                        )
                      }
                      disabled={loading || saving}
                      className="w-full"
                      minFractionDigits={0}
                      maxFractionDigits={0}
                      step={1}
                    />
                  </div>
                </div>
                <p
                  className={`text-sm ${
                    errors.person_shift_restrictions
                      ?.maxConsecutiveDaysWithShifts
                      ? 'text-red-500'
                      : 'text-transparent'
                  }`}
                  style={{
                    height: '20px',
                    margin: 0,
                    marginLeft: 'auto',
                    width: 'fit-content'
                  }}
                >
                  {errors.person_shift_restrictions
                    ?.maxConsecutiveDaysWithShifts
                    ? errors.person_shift_restrictions
                        .maxConsecutiveDaysWithShifts.message
                    : ' '}
                </p>
              </div>

              <div className="flex flex-column">
                <div className="globalSettings-row flex align-items-center justify-content-between gap-3">
                  <label htmlFor="maxMonthlyHours" className="font-medium">
                    Максимальное количество запланированных часов по сотруднику
                    за месяц
                  </label>
                  <div
                    className="global-settings-input-wrapper"
                    style={{ width: '50px' }}
                  >
                    <InputNumber
                      id="maxMonthlyHours"
                      value={maxMonthlyScheduledHours}
                      onValueChange={(e) =>
                        setValue(
                          'person_shift_restrictions.maxMonthlyScheduledHours',
                          e.value ?? 0,
                          {
                            shouldDirty: true,
                            shouldValidate: true
                          }
                        )
                      }
                      disabled={loading || saving}
                      className="w-full"
                      minFractionDigits={0}
                      maxFractionDigits={0}
                      step={1}
                    />
                  </div>
                </div>
                <p
                  className={`text-sm ${
                    errors.person_shift_restrictions?.maxMonthlyScheduledHours
                      ? 'text-red-500'
                      : 'text-transparent'
                  }`}
                  style={{
                    height: '20px',
                    margin: 0,
                    marginLeft: 'auto',
                    width: 'fit-content'
                  }}
                >
                  {errors.person_shift_restrictions?.maxMonthlyScheduledHours
                    ? errors.person_shift_restrictions.maxMonthlyScheduledHours
                        .message
                    : ' '}
                </p>
                <div className="flex flex-column">
                  <div className="globalSettings-row flex align-items-center justify-content-between gap-3">
                    <label htmlFor="maxMonthlyHours" className="font-medium">
                      Настройка расписания запуска распределения нарядов по
                      участкам
                    </label>
                    <Button
                      icon="pi pi-cog"
                      onClick={() =>
                        setShowDepartmentAssignmentScheduleDialog(true)
                      }
                      type="button"
                      style={{ width: 50 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </VisibilityWrapper>
      </Dialog>

      <ConfirmationDialog
        visible={showSaveConfirmation}
        onCancel={handleCancelSave}
        onContinue={handleConfirmSave}
        header="Подтверждение сохранения"
        message="Вы уверены, что хотите сохранить изменения? Данные настройки распространяются на всю систему."
        confirmSeverity="danger"
      />

      <ConfirmationDialog
        visible={showUnsavedDialog}
        onCancel={() => setShowUnsavedDialog(false)}
        onContinue={handleContinueWithoutSaving}
        message="Есть несохранённые изменения. Вы хотите продолжить без сохранения?"
      />

      <DepartmentAssignmentScheduleDialog
        visible={showDepartmentAssignmentScheduleDialog}
        onCancel={() => setShowDepartmentAssignmentScheduleDialog(false)}
        departmentAssignmentSchedule={departmentAssignmentSchedule ?? null}
        onSave={handleDepartmentAssignmentSave}
      />
    </>
  );
};

export default GlobalSettingsDialog;
